import React from 'react';
import { Box, TextField, Typography, Grid, Slider, InputAdornment, Button, Accordion, AccordionSummary, AccordionDetails, Alert } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BudgetingStyleSelector from './BudgetingStyleSelector';
import InvestmentStrategySelector from './InvestmentStrategySelector';

/**
 * Journey Metrics Panel
 * 🎛️ Interactive controls to adjust journey projections
 */
const JourneyMetricsPanel = ({
  currentAmount = 0,
  targetAmount = 0,
  monthlySavings = 0,
  monthlyIncome = 0, // Net income
  grossMonthlyIncome = 0, // Gross income
  monthlyExpenses = 0,
  interestRate = 4.0,
  retirementAge: propRetirementAge = 65,
  onCurrentAmountChange,
  onTargetAmountChange,
  onMonthlySavingsChange,
  onExpensesChange,
  onInterestRateChange,
  onRetirementAgeChange
}) => {
  const [budgetingStyle, setBudgetingStyle] = React.useState('50_30_20');
  const [customPercentages, setCustomPercentages] = React.useState({ needs: 50, wants: 30, savings: 20 });
  const [localExpenses, setLocalExpenses] = React.useState(monthlyExpenses);
  const [retirementAge, setRetirementAge] = React.useState(propRetirementAge);
  const [investmentStrategy, setInvestmentStrategy] = React.useState('savings_account');
  
  // Sync retirement age with prop changes
  React.useEffect(() => {
    setRetirementAge(propRetirementAge);
  }, [propRetirementAge]);
  
  // Sync local expenses with prop changes
  React.useEffect(() => {
    setLocalExpenses(monthlyExpenses);
  }, [monthlyExpenses]);

  // Calculate recommended based on budgeting style
  const getRecommendedByStyle = (style) => {
    if (!monthlyIncome || monthlyIncome <= 0) return { expenses: 0, savings: 0 };
    
    switch (style) {
      case '50_30_20':
        return { expenses: monthlyIncome * 0.8, savings: monthlyIncome * 0.2 };
      case '60_20_20':
        return { expenses: monthlyIncome * 0.8, savings: monthlyIncome * 0.2 };
      case '70_20_10':
        return { expenses: monthlyIncome * 0.9, savings: monthlyIncome * 0.1 };
      case 'zero_based':
      case 'envelope':
        return { expenses: monthlyIncome * 0.8, savings: monthlyIncome * 0.2 };
      case 'custom':
        return { 
          expenses: monthlyIncome * ((customPercentages.needs + customPercentages.wants) / 100), 
          savings: monthlyIncome * (customPercentages.savings / 100) 
        };
      default:
        return { expenses: monthlyIncome * 0.8, savings: monthlyIncome * 0.2 };
    }
  };

  // Calculate recommended savings for retirement (default 65 years)
  const calculateRetirementRecommendedSavings = () => {
    if (!monthlyIncome || monthlyIncome <= 0) return 0;
    
    // Calculate years until retirement
    const currentAge = 30; // Default, should come from journey data
    const yearsToRetirement = retirementAge - currentAge;
    
    if (yearsToRetirement <= 0) return 0;
    
    // Recommended: Save 15-20% of income for retirement
    // Adjust based on years until retirement (more aggressive if less time)
    const basePercentage = yearsToRetirement >= 30 ? 0.15 : 0.20;
    const retirementRecommended = monthlyIncome * basePercentage;
    
    // Also consider available income after expenses
    const available = monthlyIncome - localExpenses;
    
    // Return the recommended amount, but don't exceed available
    return Math.max(0, Math.min(retirementRecommended, available));
  };

  // Calculate recommended savings based on budgeting style
  const calculateRecommendedSavings = () => {
    // For retirement goals, use retirement calculation
    // For other goals, use budgeting style
    const retirementRecommended = calculateRetirementRecommendedSavings();
    const styleRecommended = getRecommendedByStyle(budgetingStyle);
    
    // Use the higher of the two recommendations
    const recommended = Math.max(retirementRecommended, styleRecommended.savings);
    const available = monthlyIncome - localExpenses;
    
    return Math.max(0, Math.min(recommended, available));
  };

  const recommendedSavings = calculateRecommendedSavings();
  // Calculate time to goal (no maximum limit)
  const calculateTimeToGoal = () => {
    if (!monthlySavings || monthlySavings <= 0) return 'N/A';
    
    const monthlyRate = (interestRate / 100) / 12;
    const remaining = targetAmount - currentAmount;
    
    if (remaining <= 0) return 'Goal Achieved!';
    
    let months = 0;
    let amount = currentAmount;
    const maxMonths = 1200; // 100 years max (practical limit)
    
    while (amount < targetAmount && months < maxMonths) {
      amount = amount * (1 + monthlyRate) + monthlySavings;
      months++;
      if (amount >= targetAmount) break;
    }
    
    if (months >= maxMonths) return '100+ years';
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) return `${months} months`;
    if (remainingMonths === 0) return `${years} year${years > 1 ? 's' : ''}`;
    return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
  };

  // Calculate total contributions needed - must match chart calculation exactly
  const calculateTotalContributions = () => {
    if (!monthlySavings || monthlySavings <= 0) return 0;
    
    const monthlyRate = (interestRate / 100) / 12;
    const remaining = targetAmount - currentAmount;
    
    if (remaining <= 0) return 0;
    
    let months = 0;
    let amount = currentAmount;
    const maxMonths = 1200; // Match chart's maxMonths (100 years)
    let totalContributions = 0;
    
    while (amount < targetAmount && months < maxMonths) {
      amount = amount * (1 + monthlyRate) + monthlySavings;
      totalContributions += monthlySavings;
      months++;
      if (amount >= targetAmount) break;
    }
    
    return totalContributions;
  };
  
  // Calculate interest earned - must match chart calculation exactly
  const calculateInterestEarned = () => {
    if (!monthlySavings || monthlySavings <= 0) return 0;
    
    const monthlyRate = (interestRate / 100) / 12;
    const remaining = targetAmount - currentAmount;
    
    if (remaining <= 0) return 0;
    
    let months = 0;
    let amount = currentAmount;
    const maxMonths = 1200; // Match chart's maxMonths
    let totalInterest = 0;
    
    while (amount < targetAmount && months < maxMonths) {
      const interestEarned = amount * monthlyRate;
      amount = amount * (1 + monthlyRate) + monthlySavings;
      totalInterest += interestEarned;
      months++;
      if (amount >= targetAmount) break;
    }
    
    return totalInterest;
  };

  const timeToGoal = calculateTimeToGoal();
  
  // Calculate time to goal in months for chart timeline
  const calculateTimeToGoalMonths = () => {
    if (!monthlySavings || monthlySavings <= 0) return 0;
    
    const monthlyRate = (interestRate / 100) / 12;
    const remaining = targetAmount - currentAmount;
    
    if (remaining <= 0) return 0;
    
    let months = 0;
    let amount = currentAmount;
    const maxMonths = 1200; // 100 years max
    
    while (amount < targetAmount && months < maxMonths) {
      amount = amount * (1 + monthlyRate) + monthlySavings;
      months++;
      if (amount >= targetAmount) break;
    }
    
    return months >= maxMonths ? 1200 : months;
  };
  
  const timeToGoalMonths = calculateTimeToGoalMonths();
  const totalContributions = calculateTotalContributions();
  const interestEarned = calculateInterestEarned(); // Use calculated interest, not difference
  
  // Validation: Log Projection Summary values for comparison with chart
  React.useEffect(() => {
    console.log('📋 [JourneyMetricsPanel] Projection Summary values:', {
      timeToGoal,
      timeToGoalMonths,
      totalContributions: totalContributions.toFixed(2),
      interestEarned: interestEarned.toFixed(2),
      finalAmount: targetAmount.toFixed(2),
      currentAmount: currentAmount.toFixed(2),
      monthlySavings: monthlySavings.toFixed(2),
      interestRate: interestRate.toFixed(2) + '%',
      expectedTotal: (currentAmount + totalContributions + interestEarned).toFixed(2),
      matchesTarget: Math.abs((currentAmount + totalContributions + interestEarned) - targetAmount) < 1
    });
  }, [timeToGoal, timeToGoalMonths, totalContributions, interestEarned, targetAmount, currentAmount, monthlySavings, interestRate]);

  // Apply recommended savings when user clicks
  const handleApplyRecommended = () => {
    if (recommendedSavings > 0) {
      onMonthlySavingsChange(recommendedSavings);
    }
  };

  // Real-time validation
  const expensesExceedIncome = localExpenses > monthlyIncome && monthlyIncome > 0;
  const savingsExceedIncome = monthlySavings > monthlyIncome && monthlyIncome > 0;
  const negativeRemaining = (monthlyIncome - localExpenses - monthlySavings) < 0;

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Adjust Metrics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Change these values to see how it affects your timeline
      </Typography>

      {/* Validation Warnings */}
      {expensesExceedIncome && (
        <Alert severity="error" sx={{ mb: 2 }}>
          ⚠️ Monthly expenses (${localExpenses.toLocaleString()}) exceed monthly income (${monthlyIncome.toLocaleString()})
        </Alert>
      )}
      {savingsExceedIncome && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ Monthly savings (${monthlySavings.toLocaleString()}) exceed monthly income (${monthlyIncome.toLocaleString()})
        </Alert>
      )}
      {negativeRemaining && !expensesExceedIncome && !savingsExceedIncome && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ Expenses + Savings exceed income. Remaining: ${(monthlyIncome - localExpenses - monthlySavings).toLocaleString()}
        </Alert>
      )}

      {/* Section 1: Income & Expenses */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Income & Expenses</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <BudgetingStyleSelector
        selectedStyle={budgetingStyle}
        monthlyIncome={monthlyIncome}
        grossMonthlyIncome={grossMonthlyIncome}
        monthlyExpenses={localExpenses}
        monthlySavings={monthlySavings}
        onStyleChange={(style) => {
          setBudgetingStyle(style);
          // Auto-apply recommended values when style changes
          const recommended = getRecommendedByStyle(style);
          setLocalExpenses(recommended.expenses);
          onExpensesChange(recommended.expenses);
          onMonthlySavingsChange(recommended.savings);
        }}
        onExpensesChange={(value) => {
          setLocalExpenses(value);
          onExpensesChange(value);
        }}
        onSavingsChange={onMonthlySavingsChange}
        customPercentages={customPercentages}
        onCustomPercentagesChange={(percentages) => {
          setCustomPercentages(percentages);
          if (budgetingStyle === 'custom') {
            const expenses = monthlyIncome * ((percentages.needs + percentages.wants) / 100);
            const savings = monthlyIncome * (percentages.savings / 100);
            setLocalExpenses(expenses);
            onExpensesChange(expenses);
            onMonthlySavingsChange(savings);
          }
        }}
          />
        </AccordionDetails>
      </Accordion>

      {/* Section 2: Investment Strategy */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Investment Strategy</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Expected Annual Return - Above Strategy Selector */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Expected Annual Return: {interestRate.toFixed(2)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Adjust the rate above or change investment strategy to update this value
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontStyle: 'italic' }}>
              Note: For savings accounts, this is APY (already compounded). For investments, this is expected annual return.
            </Typography>
            <Slider
              value={interestRate}
              onChange={(e, newValue) => onInterestRateChange(newValue)}
              min={0}
              max={15}
              step={0.1}
              marks={[
                { value: 0, label: '0%' },
                { value: 5, label: '5%' },
                { value: 10, label: '10%' },
                { value: 15, label: '15%' }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value.toFixed(1)}%`}
            />
          </Box>
          
          <InvestmentStrategySelector
            selectedStrategy={investmentStrategy}
            interestRate={interestRate}
            onStrategyChange={(strategy) => {
              setInvestmentStrategy(strategy);
            }}
            onInterestRateChange={onInterestRateChange}
          />
        </AccordionDetails>
      </Accordion>

      {/* Section 3: Goal Settings */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Goal Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
        {/* Current Amount */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            Current Amount
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={currentAmount}
            onChange={(e) => onCurrentAmountChange(parseFloat(e.target.value) || 0)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            size="small"
          />
        </Grid>

        {/* Target Amount */}
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            Target Amount
          </Typography>
            <TextField
              fullWidth
              type="number"
              value={Math.round(targetAmount)}
              onChange={(e) => onTargetAmountChange(parseFloat(e.target.value) || 0)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              size="small"
            />
        </Grid>

        {/* Length of Time (Years) */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Length of Time: {Math.floor(timeToGoalMonths / 12)} years {timeToGoalMonths % 12} months
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Adjust target amount or monthly savings to change timeline
          </Typography>
          <Box sx={{ px: 1 }}>
            <Slider
              value={Math.min(Math.floor(timeToGoalMonths / 12), 60)}
              onChange={(e, newValue) => {
                // Calculate target amount based on selected years
                const years = newValue;
                const monthlyRate = (interestRate / 100) / 12;
                const totalMonths = years * 12;
                
                // Calculate what target amount would be needed for this timeline
                // Using reverse calculation: FV = PV * (1 + r)^n + PMT * [((1 + r)^n - 1) / r]
                const compoundFactor = Math.pow(1 + monthlyRate, totalMonths);
                const futureValue = currentAmount * compoundFactor + monthlySavings * ((compoundFactor - 1) / monthlyRate);
                onTargetAmountChange(Math.round(futureValue));
              }}
              min={0}
              max={60}
              step={1}
              marks={[
                { value: 0, label: '0' },
                { value: 10, label: '10' },
                { value: 20, label: '20' },
                { value: 30, label: '30' },
                { value: 40, label: '40' },
                { value: 50, label: '50' },
                { value: 60, label: '60' }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value} years`}
            />
          </Box>
        </Grid>



        {/* Projection Summary */}
        <Grid item xs={12}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Projection Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Time to Goal
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                  {timeToGoal}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Contributions
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  ${totalContributions.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Interest Earned
                </Typography>
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                  ${interestEarned.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Final Amount
                </Typography>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                  ${targetAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default JourneyMetricsPanel;

